/**
 * 流式 TTS 客户端(web 版)—— 复刻小程序 utils/tts-realtime-aliyun.js 的 StreamingTTS。
 *
 * 对外 API 一致:new StreamingTTS({persona}) + enqueue / abort / untilIdle + on('playStart'|'idle'|'error')。
 * 行为一致:每句立刻 fire 合成(并行),按队列顺序串行播放;abort 用 gen 计数器让 in-flight 静默退出。
 *
 * 与小程序的差异(都是平台 API 不同,不是行为不同):
 *   - 合成走服务端代理 POST(api.tts.synthesizeAliyun),拿整段 mp3 ArrayBuffer——浏览器不能直连 DashScope WS
 *   - 播放用 HTMLAudioElement + Blob URL,替代小程序的临时文件 + innerAudioContext
 *
 * audio 元素挂在 DOM(隐藏)而非游离:个别浏览器对游离 media 元素的播放策略更严,挂上更稳,
 * 也便于端到端测试观测 paused/currentTime。dispose() 在组件卸载时移除它。
 */

import { api } from './api'

type TTSEvent = 'playStart' | 'idle' | 'error'

interface QueueItem {
    index: number
    audioPromise: Promise<ArrayBuffer>
}

export class StreamingTTS {
    private persona: string
    private queue: QueueItem[] = []
    private playing = false
    private handlers: Partial<Record<TTSEvent, (...args: unknown[]) => void>> = {}
    // 每次 abort +1;in-flight 的 _playNext 在每次 await 后比对,对不上就静默退出
    private gen = 0
    private audio: HTMLAudioElement
    private currentUrl: string | null = null
    private idleResolvers: Array<() => void> = []
    private captured: ArrayBuffer[] = [] // 本轮已播音频(按播放顺序),回合结束由 takeCaptured 取走落库;abort 清空

    constructor({ persona }: { persona: string }) {
        if (!persona) throw new Error('StreamingTTS: persona is required')
        this.persona = persona

        this.audio = document.createElement('audio')
        this.audio.hidden = true
        this.audio.onended = () => this._playNext()
        this.audio.onerror = () => {
            this._emit('error', new Error('audio playback error'))
            this._playNext()
        }
        document.body.appendChild(this.audio)
    }

    on(event: TTSEvent, handler: (...args: unknown[]) => void): this {
        this.handlers[event] = handler
        return this
    }

    enqueue(text: string, index: number): void {
        // 立刻 fire 合成,不等播放——下一句的合成与当前句播放并行
        const audioPromise = this._synthesize(text)
        audioPromise.catch(() => {}) // 挡未消费的 rejection
        this.queue.push({ index, audioPromise })
        if (!this.playing) this._playNext()
    }

    abort(): void {
        this.gen++
        this.queue = []
        this.captured = [] // 打断 = 本轮不落库,丢弃已播片段
        try {
            this.audio.pause()
        } catch {
            /* ignore */
        }
        this._releaseUrl()
        this.playing = false
        this._resolveIdle()
    }

    /** 取走本轮已播音频(按顺序)并清空。回合正常结束时由 turnRunner 调用 → 落库。 */
    takeCaptured(): ArrayBuffer[] {
        const c = this.captured
        this.captured = []
        return c
    }

    /** 组件卸载时调用:停一切 + 从 DOM 摘掉 audio 元素。 */
    dispose(): void {
        this.abort()
        this.audio.onended = null
        this.audio.onerror = null
        this.audio.remove()
    }

    untilIdle(): Promise<void> {
        if (!this.playing && this.queue.length === 0) return Promise.resolve()
        return new Promise((resolve) => {
            this.idleResolvers.push(resolve)
        })
    }

    private _resolveIdle(): void {
        if (this.idleResolvers.length === 0) return
        const rs = this.idleResolvers
        this.idleResolvers = []
        rs.forEach((r) => r())
    }

    private async _playNext(): Promise<void> {
        if (this.queue.length === 0) {
            this.playing = false
            this._emit('idle')
            this._resolveIdle()
            return
        }
        this.playing = true
        const gen = this.gen
        const { index, audioPromise } = this.queue.shift()!
        try {
            const buf = await audioPromise
            if (gen !== this.gen) return
            this.captured.push(buf) // 真正要播的句子才记(被 abort 的 gen 在上面已 return)
            this._releaseUrl()
            this.currentUrl = URL.createObjectURL(new Blob([buf], { type: 'audio/mpeg' }))
            this.audio.src = this.currentUrl
            await this.audio.play()
            this._emit('playStart', index)
        } catch (err) {
            if (gen !== this.gen) return
            this._emit('error', err)
            this._playNext()
        }
    }

    private _synthesize(text: string): Promise<ArrayBuffer> {
        return api.tts.synthesizeAliyun(text, this.persona)
    }

    private _releaseUrl(): void {
        if (this.currentUrl) {
            URL.revokeObjectURL(this.currentUrl)
            this.currentUrl = null
        }
    }

    private _emit(event: TTSEvent, ...args: unknown[]): void {
        const handler = this.handlers[event]
        if (handler) {
            try {
                handler(...args)
            } catch (e) {
                console.error('[tts] handler threw', e)
            }
        }
    }
}
